import { useRemoteMetrics, useRemoteTickets, useRemoteUsers, useActivityBasis, activityBasisNote } from '../lib/useRemoteData'
import { useAuth } from '../lib/auth'
import { useLatestRelease } from '../lib/useLatestRelease'
import { mrrBasis, summariseMrr, isGranted, isCreditFunded } from '../lib/revenue'
import { tierName, tierTone } from '../lib/tiers'
import { LoadError } from '../components/LoadError'
import { dateTime, num, usd } from '../lib/format'
import { DetailGrid, useOpenRow } from '../components/RowDetail'
import { Drillable, DrillValue } from '../components/Drill'
import { GoalTile } from '../components/GoalTile'
import { billingLabel, trialBadge, verifiedLabel } from '../lib/userLabels'
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'

function Tile({ label, value, delta, hint }: { label: string; value: string; delta?: string; hint?: string }) {
  return (
    <div className="glass-card rounded-xl p-5">
      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <div className="text-2xl font-semibold mt-2">{value}</div>
      {delta && <div className="text-xs text-accent mt-1">{delta}</div>}
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </div>
  )
}

// A list item that opens to its detail underneath, the way table rows do
// (components/RowDetail.tsx): click or Enter toggles it, a control inside it
// keeps its own click.
const INTERACTIVE = 'button, a, input, select, textarea, label, summary, [role="switch"], [data-no-row-toggle]'

function OpenableItem({ open, onToggle, detail, children, className, testId }: { open: boolean; onToggle: () => void; detail: () => ReactNode; children: ReactNode; className: string; testId: string }): JSX.Element {
  const onClick = (e: MouseEvent<HTMLDivElement>): void => {
    const control = (e.target as Element).closest(INTERACTIVE)
    if (control && e.currentTarget.contains(control)) return
    if (window.getSelection()?.toString()) return
    onToggle()
  }
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
    e.preventDefault()
    onToggle()
  }
  return (
    <li>
      <div className={`${className} cursor-pointer rounded-md -mx-2 px-2 hover:bg-white/[0.02]`} role="button" tabIndex={0} aria-expanded={open} onClick={onClick} onKeyDown={onKeyDown} data-testid={testId}>
        {children}
      </div>
      {open && <div className="pb-3 px-1" data-testid={`${testId}-detail`}>{detail()}</div>}
    </li>
  )
}

export default function Dashboard() {
  const openItem = useOpenRow()
  const session = useAuth()
  const release = useLatestRelease()
  const { data: metrics, error: metricsError, loading: metricsLoading } = useRemoteMetrics()
  const { data: tickets, error: ticketsError, loading: ticketsLoading } = useRemoteTickets()
  const { data: users, error: usersError, loading: usersLoading } = useRemoteUsers()
  // No fallback numbers. When a read fails, its tiles say "unavailable" and the
  // error is shown above them; when there is genuinely no data yet (a fresh
  // database), they say so. A row of zeros is shown only when zero is real.
  const last = metrics[metrics.length - 1]
  const prev = metrics[metrics.length - 2]
  const openTickets = tickets.filter((t) => t.status === 'open' || t.status === 'pending').length
  const urgentTickets = tickets.filter((t) => t.priority === 'urgent' && t.status !== 'closed').length
  // Revenue comes only from live subscriptions, at the price actually charged
  // where the server reports it; see lib/revenue.ts for the rules and why list
  // prices are no longer a safe stand-in after the October 2026 price change.
  const mrr = summariseMrr(users)
  const grantedCount = users.filter(isGranted).length
  const creditFundedCount = users.filter(isCreditFunded).length
  const delta = (a: number, b: number | undefined): string | undefined =>
    b === undefined ? undefined : `${a - b >= 0 ? '+' : ''}${a - b} vs previous day`
  const metricsTile = (pick: (m: NonNullable<typeof last>) => number) =>
    metricsError ? '—' : metricsLoading ? '…' : last ? pick(last).toLocaleString() : '—'
  const activityBasis = useActivityBasis()
  const metricsHint = metricsError ? 'Unavailable: metrics failed to load' : !metricsLoading && !last ? 'No daily metrics recorded yet' : undefined

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Overview</div>
        <h1 className="text-2xl font-semibold mt-1">Welcome back, {session?.name}</h1>
        <div className="text-sm text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
          <span>{last ? `Snapshot of the Plexii network as of ${last.date}.` : 'Snapshot of the Plexii network.'}</span>
          {/* Which build customers are on — the first question on most tickets.
              Absent rather than guessed when the feed cannot be read. */}
          {release && (
            <a
              href={release.url}
              target="_blank"
              rel="noreferrer"
              data-testid="dashboard-latest-release"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-xs text-slate-300 hover:text-white hover:bg-white/10"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              Shipping v{release.version}
              {release.publishedAt && (
                <span className="text-slate-500">
                  · {new Date(release.publishedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                </span>
              )}
            </a>
          )}
        </div>
      </div>
      {metricsError && <LoadError what="daily metrics" endpoint="GET /admin/metrics" error={metricsError} testId="dashboard-metrics-error" />}
      {usersError && <LoadError what="users and billing" endpoint="GET /admin/users" error={usersError} testId="dashboard-users-error" />}
      {ticketsError && <LoadError what="tickets" endpoint="GET /admin/tickets" error={ticketsError} testId="dashboard-tickets-error" />}
      <GoalTile />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Drillable
          to={{ set: 'activeDays', filters: last ? { day: last.date } : {} }}
          label={last ? `Active accounts on ${last.date}` : 'Active accounts'}
          expect={last && !metricsError ? { key: 'count', value: last.dau } : undefined}
          testId="dash-tile-active"
        >
          <Tile label="Active accounts, floor (latest day)" value={metricsTile((m) => m.dau)} delta={last && !metricsError ? delta(last.dau, prev?.dau) : undefined} hint={metricsHint ?? activityBasisNote(activityBasis)} />
        </Drillable>
        <Drillable
          to={{ set: 'accounts', filters: last ? { createdOn: last.date } : {} }}
          label={last ? `Signups on ${last.date}` : 'Signups'}
          expect={last && !metricsError ? { key: 'count', value: last.signups } : undefined}
          testId="dash-tile-signups"
        >
          <Tile label="Signups (latest day)" value={metricsTile((m) => m.signups)} delta={last && !metricsError ? delta(last.signups, prev?.signups) : undefined} hint={metricsHint} />
        </Drillable>
        <Drillable
          to={{ set: 'tickets', filters: { status: 'open,pending' } }}
          label="Open tickets"
          expect={!ticketsError && !ticketsLoading ? { key: 'count', value: openTickets } : undefined}
          testId="dash-tile-tickets"
        >
          <Tile
            label="Open tickets"
            value={ticketsError ? '—' : ticketsLoading ? '…' : String(openTickets)}
            hint={ticketsError ? 'Unavailable: tickets failed to load' : urgentTickets ? `${urgentTickets} urgent` : ticketsLoading ? undefined : 'None urgent'}
          />
        </Drillable>
        {/* The revenue is worked out from each paying account's billing
            (lib/revenue.ts), so its records are those accounts, each showing
            what it is charged; the server picks them by the same rule. */}
        <Drillable
          to={{ set: 'accounts', filters: { paying: '1' } }}
          label="Paying accounts behind monthly recurring revenue"
          expect={!usersError && !usersLoading ? { key: 'count', value: mrr.paying } : undefined}
          testId="dash-tile-mrr"
        >
          <Tile
            label="Monthly recurring revenue"
            value={usersError ? '—' : usersLoading ? '…' : usd(mrr.totalUsd)}
            hint={
              usersError
                ? 'Unavailable: users failed to load'
                : usersLoading
                  ? undefined
                  : `${mrr.paying} paying${creditFundedCount ? ` · ${creditFundedCount} on referral credit` : ''}${grantedCount ? ` · ${grantedCount} granted (no charge)` : ''} · ${mrrBasis(mrr)}`
            }
          />
        </Drillable>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-card rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">Recent tickets</div>
          {!ticketsError && !ticketsLoading && tickets.length === 0 && <div className="text-sm text-slate-500">No tickets.</div>}
          <ul className="divide-y divide-white/5">
            {tickets.slice(0, 4).map((t) => (
              <OpenableItem
                key={t.id}
                open={openItem.isOpen(`t:${t.id}`)}
                onToggle={() => openItem.toggle(`t:${t.id}`)}
                className="py-3 flex items-start justify-between gap-3"
                testId={`dash-ticket-${t.id}`}
                detail={() => (
                  <DetailGrid
                    facts={[
                      ['From', t.userEmail],
                      ['Status', t.status],
                      ['Priority', t.priority],
                      ['Category', t.category],
                      ['Opened', dateTime(t.createdAt)],
                      ['Last change', dateTime(t.updatedAt)],
                      ['Replies', num(t.replyCount)],
                      ['Ticket id', <DrillValue to={{ set: 'tickets', filters: { id: t.id } }} label={`Ticket “${t.subject}”`}><span className="font-mono break-all">{t.id}</span></DrillValue>],
                      ['Account', t.userId === 'unknown' ? 'No account linked' : <DrillValue to={{ set: 'accounts', filters: { id: t.userId } }} label={`Account ${t.userEmail}`}><span className="font-mono break-all">{t.userId}</span></DrillValue>]
                    ]}
                    wide={[['Message', t.body]]}
                  />
                )}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.subject}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{t.userEmail} · {t.category}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  t.priority === 'urgent' ? 'bg-red-500/15 text-red-300' :
                  t.priority === 'high' ? 'bg-orange-500/15 text-orange-300' :
                  t.priority === 'normal' ? 'bg-accent/15 text-accent' : 'bg-slate-500/15 text-slate-300'
                }`}>{t.priority}</span>
              </OpenableItem>
            ))}
          </ul>
        </div>
        <div className="glass-card rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">Latest signups</div>
          {!usersError && !usersLoading && users.length === 0 && <div className="text-sm text-slate-500">No accounts yet.</div>}
          <ul className="divide-y divide-white/5">
            {[...users]
              .sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0) || (a.createdAt < b.createdAt ? 1 : -1))
              .slice(0, 4)
              .map((u) => (
                <OpenableItem
                  key={u.id}
                  open={openItem.isOpen(`u:${u.id}`)}
                  onToggle={() => openItem.toggle(`u:${u.id}`)}
                  className="py-3 flex items-center justify-between"
                  testId={`dash-user-${u.id}`}
                  detail={() => (
                    <DetailGrid
                      facts={[
                        ['Email', u.email],
                        ['Handle', u.handle],
                        ['Tier', tierName(u.tier)],
                        ['Billing', billingLabel(u)],
                        ['Email confirmed', verifiedLabel(u)],
                        ['Trial', trialBadge(u).label],
                        ['Status', u.status],
                        ['Joined', u.createdAt],
                        // The day alone when an older server sends no time: never an invented midnight.
                        ['Last login', u.lastLoginAtMs ? dateTime(u.lastLoginAtMs) : u.lastLoginAt ?? 'Never'],
                        ['Sign-ins', <DrillValue to={{ set: 'sessions', filters: { account: u.id } }} label={`Sign-ins of ${u.email}`} expect={{ key: 'count', value: u.deviceCount }}>{num(u.deviceCount)}</DrillValue>],
                        ['Share links', <DrillValue to={{ set: 'shares', filters: { fromAccount: u.id } }} label={`Share links from ${u.email}`} expect={{ key: 'count', value: u.shareCount }}>{num(u.shareCount)}</DrillValue>],
                        ['App', u.telemetryVersion ? `${u.telemetryVersion}${u.telemetryPlatform ? ` on ${u.telemetryPlatform}` : ''}` : 'Not reported'],
                        ['Account id', <DrillValue to={{ set: 'accounts', filters: { id: u.id } }} label={`Account ${u.email}`}><span className="font-mono break-all">{u.id}</span></DrillValue>]
                      ]}
                    />
                  )}
                >
                  <div>
                    <div className="text-sm font-medium">{u.email}</div>
                    <div className="text-xs text-slate-400 mt-0.5">Joined {u.createdAt}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(u.tier)}`}>{tierName(u.tier)}</span>
                </OpenableItem>
              ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
